import { useCallback, useEffect, useState } from 'react';
import type { ConversationDocsDto } from '@chat-emp/shared';

import { cidParts } from '../lib/bridge.js';
import type { DrawnSummary } from './doc-rules.js';
import { getDocs, saveNote as saveNoteApi, saveSummary as saveSummaryApi } from '../lib/facade.js';
import { nudgeAssistant } from '../hooks/useAssistant.js';

/**
 * What is written about one conversation, and the operations that change it.
 *
 * Per channel rather than one store for the app: the details panel is keyed on
 * the channel id, so it remounts when the conversation changes, and nothing else
 * reads these. The prompt that consumes the note is assembled server-side, which
 * is what leaves a single reader here.
 *
 * Writes are confirmed, not optimistic. A note is worth more than the tenth of a
 * second saved by pretending: if the write fails, the caller still holds the
 * text and can put it back on screen, which an optimistic update that already
 * closed the editor cannot.
 */
export interface UseDocs {
  docs: ConversationDocsDto;
  /** The first read is still in flight — the entries show skeletons, not "nothing written". */
  loading: boolean;
  /** The last read failed. Writes report their own failure to the caller instead. */
  error: boolean;
  /** Resolves once stored. Rejects if the write failed, with the draft still the caller's. */
  saveNote: (text: string) => Promise<void>;
  /** Persist a drawn summary. `generatedAt` is stamped here, on the keep. */
  saveSummary: (drawn: DrawnSummary) => Promise<void>;
}

const EMPTY: ConversationDocsDto = { note: null, summary: null };

/**
 * A channel arrives here as a Stream cid ("messaging:chat-comp-1-js-2"), and the
 * upstream stores channels under the bare id. Sending the cid matches nothing,
 * which comes back as "not a member of that channel" — a 404 that reads exactly
 * like a permission problem and is nothing of the sort.
 *
 * Stripped here rather than in the BFF for the same reason `endChat` and
 * `setApplicantStatus` strip on this side: the client is what holds a cid, and
 * a BFF that quietly accepted a prefixed id would make the next caller's
 * mistake invisible too.
 */
export function useDocs(cid: string): UseDocs {
  const channelId = cidParts(cid).channelId;
  const [docs, setDocs] = useState<ConversationDocsDto>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // A response landing after the panel moved on would write another
    // conversation's note into this one's box.
    let active = true;
    setLoading(true);
    setError(false);
    setDocs(EMPTY);

    getDocs(channelId)
      .then((loaded) => {
        if (active) setDocs(loaded);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [channelId]);

  const saveNote = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      await saveNoteApi({ channelId, text: trimmed });
      nudgeAssistant(channelId);
      setDocs((current) => ({
        ...current,
        note: trimmed ? { text: trimmed, updatedAt: new Date().toISOString() } : null,
      }));
    },
    [channelId],
  );

  const saveSummary = useCallback(
    async (drawn: DrawnSummary) => {
      await saveSummaryApi({ channelId, ...drawn });
      nudgeAssistant(channelId);
      // Stamp the keep time optimistically; the next read returns the server's.
      setDocs((current) => ({
        ...current,
        summary: { ...drawn, generatedAt: new Date().toISOString() },
      }));
    },
    [channelId],
  );

  return { docs, loading, error, saveNote, saveSummary };
}
